import { ChangeDetectionStrategy, Component, computed, inject, input, output, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Tag } from '@nusign/picflow-backend-types';
import { startWith, take, tap } from 'rxjs';
import { TagService } from '../../../services/tag.service';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { AutofocusDirective } from '../../../shared/directives/autofocus.directive';
import { ButtonDirective } from '../../../shared/directives/button.directive';

@Component({
  selector: 'app-asset-tag-select',
  templateUrl: './asset-tag-select.component.html',
  styleUrls: ['./asset-tag-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, SvgComponent, ButtonDirective, ContextMenuComponent, AutofocusDirective],
})
export class AssetTagSelectComponent {
  private tagService = inject(TagService);

  readonly tags = input.required<Tag[]>();
  readonly unavailableTags = input.required<Tag[]>();
  readonly lightBackground = input(false);
  readonly noAddTagButton = input(false);
  readonly addTextPlaceholder = input('');
  readonly canCreate = input.required<boolean>();
  readonly add = output<Tag>();
  readonly tagTypeahead = viewChild<ContextMenuComponent>('tagTypeahead');

  addTagForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    category: new FormControl(''),
  });

  canCreateExtra = computed(() => {
    const allTags = this.tags();
    const tagValue = this.tagValue();
    if (!allTags || !tagValue) return false;
    const input = tagValue.toLowerCase();
    const alreadyExists = allTags.some(tag => tag.name.toLowerCase() === input);
    if (alreadyExists) return false;
    return true;
  });

  allAvailableTags = computed(() => {
    const allTags = this.tags();
    const unavailableTags = this.unavailableTags();
    if (!allTags || !unavailableTags) return [];
    return allTags.filter(tag => {
      if (unavailableTags.some(at => at.id === tag.id)) return false;
      return true;
    });
  });

  tagValue = toSignal(this.addTagForm.controls.name.valueChanges.pipe(startWith(this.addTagForm.controls.name.value)));

  filteredTags = computed(() => {
    const tags = this.allAvailableTags();
    const tagValue = this.tagValue();
    if (!tags) return [];
    if (!tagValue) return tags;
    const input = tagValue.toLowerCase();
    return tags.filter(tag => tag.name.toLowerCase().includes(input));
  });

  addTag() {
    if (!this.canCreate()) return;
    return this.tagService.tags$
      .pipe(
        take(1),
        tap(tags => {
          const existingTag = tags.find(t => t.name === this.addTagForm.value.name);
          if (existingTag) return this.emitTag(existingTag);
          if (!this.addTagForm.valid) return;
          this.tagService.createTag(this.addTagForm.value.name, this.addTagForm.value.category).subscribe(tag => {
            this.emitTag(tag);
          });
        })
      )
      .subscribe();
  }

  emitTag(tag: Tag) {
    this.add.emit(tag);
    this.tagTypeahead().close();
    this.addTagForm.reset();
  }

  isFocused(el: HTMLElement, event: MouseEvent) {
    event.stopPropagation();
    return el === document.activeElement;
  }
}
